<template>
    <div id="whatsapp_analytics_grid">
      <InfoGrid :columns="3">
        <ContainerInfoItem
          v-for="(data, key) in analyticsData"
          :key="key"
          source="marketing_whatsapp_activity_analytics"
          :title="data.title"
          :description="range?.label || ''"
          :value="formatAnalyticsValue(data.value, data.unit)"
          :is-loading="data.isLoading"
        />
      </InfoGrid>
     </div>
  </template>
  
  <script setup>
  import { computed, reactive, watch } from "vue";
  import { formatAnalyticsValue } from "@/src/utils/analyticUtils.js";
  import {
    marketingWhatsappActions,
    units,
  } from "@/src/constants/analyticConstants.js";
  import store from "@/src/store/index.js";
  import AnalyticService from "@/src/api/AnalyticService.js";
  import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
  import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
  import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";
  import dayjs from "dayjs";
  
  const range = computed(() => store.getters["analytics/getRange"]);
  const userIds = computed(() => store.getters["analytics/getUserIds"]);
  const teamIds = computed(() => store.getters["analytics/getTeamIds"]);
  
  const analyticsData = reactive([
    {
      id: marketingWhatsappActions.WHATSAPP_SENT,
      title: "WhatsApp sent",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingWhatsappActions.WHATSAPP_CLICKED,
      title: "WhatsApp clicked",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingWhatsappActions.WHATSAPP_REPLIED,
      title: "WhatsApp replied",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingWhatsappActions.WHATSAPP_BOUNCED,
      title: "Bounced",
      value: 0,
      isLoading: false,
    },
    {
      id: marketingWhatsappActions.WHATSAPP_UNSUBSCRIBED,
      title: "Unsubscribed",
      value: 0,
      isLoading: false,
    },
  ]);

  const fetchAnalytics = async () => {
    if (!range.value) {
      return;
    }

    let start, end;

    if (range.value.start && range.value.end) {
      start = range.value.start;
      end = range.value.end;
    } else {
      return;
    }

    analyticsData.forEach((item) => {
      item.isLoading = true;
    });
  
    try {
      const params = {
        start_date: start,
        end_date: end,
        channel_type: "WHATSAPP",
      };
      
      const response = await AnalyticService.V2.fetchWorkspaceMarketingCampaignStats(params);
      
      if (response?.success || response?.data || response) {
        const data = response?.data || response;
        
        const mapping = {
          [marketingWhatsappActions.WHATSAPP_SENT]: "total_sent",
          [marketingWhatsappActions.WHATSAPP_CLICKED]: "total_clicked",
          [marketingWhatsappActions.WHATSAPP_REPLIED]: "total_replied",
          [marketingWhatsappActions.WHATSAPP_BOUNCED]: "total_bounced",
          [marketingWhatsappActions.WHATSAPP_UNSUBSCRIBED]: "total_unsubscribed",
        };
        
        analyticsData.forEach((item) => {
          const apiKey = mapping[item.id] || item.id;
          let value = 0;
          
          if (data[apiKey] !== undefined && data[apiKey] !== null) {
            value = data[apiKey];
          }
          
          if (item.unit === units.RATE && value !== null && value !== undefined) {
            value = value * 100;
          }
          
          item.value = value;
        });
      }
    } catch (error) {
      console.error("An error occurred while fetching analytics: ", error);
    } finally {
      analyticsData.forEach((item) => {
        item.isLoading = false;
      });
    }
  };
  
  watch(
    [range],
    () => {
      if (range.value?.start && range.value?.end) {
        fetchAnalytics();
      }
    },
    { deep: true, immediate: true }
  );
  
  useAnalyticsRefresh(fetchAnalytics, [range, userIds, teamIds]);
  </script>
  
